/**
 * Deep dive: the sub-categories under one thread outcome (what exactly was
 * asked for, why they are holding, on what grounds they rejected), each thread
 * carrying the verbatim wording from the email that supports its label.
 * Types mirror POST comms/deepdive/* on the case delivery app.
 */
import api from '../../../helpers/apiRequester'

export type EvidenceState = 'verified' | 'needs_check' | 'human'
export type DeepDiveFlag = 'injection_suspect' | 'cost_outlier' | 'new_cap_hit' | string

export interface SuggestedAction {
    type: string | null
    label: string
    description: string | null
    timing: string | null
}

export interface Subcategory {
    id: string
    parentKey: string
    key: string
    label: string
    description: string | null
    suggestedAction: SuggestedAction | null
    source: 'llm' | 'human'
    createdBy: string | null
    mergedIntoId: string | null
    active: boolean
    createdAt: string
    updatedAt: string
    /** Threads labelled with it in the current scope. */
    threads?: number
    linked?: number
    needsCheck?: number
    flagged?: number
    last7d?: number
    lastLabelledAt?: string
}

/** What a developer needs before building the feature behind one sub-category. */
export interface SubcategoryBrief {
    /** The items the model listed most often across the threads ("photo ID", "signed LOA"…). */
    items: { detail: string; threads: number }[]
    lenders: { name: string | null; threads: number }[]
    quotes: { conversationId: string; quote: string; detail: string | null; threads: number }[]
    totals: { threads: number; fromClient: number; fromLender: number; linked: number; firstAt: string | null; lastAt: string | null }
}

export interface Evidence {
    detail: string
    quote: string
    inboundId: string | null
    offset: number | null
    verified: boolean
}

export interface ThreadLabel {
    conversationId: string
    parentKey: string
    subcategory: { id: string; key: string; label: string; description?: string | null; suggestedAction?: SuggestedAction | null }
    outcomeInboundId: string | null
    details: string[]
    evidence: Evidence[]
    evidenceState: EvidenceState
    flags: DeepDiveFlag[]
    confidence: number | null
    rationale: string | null
    model: string | null
    costPence?: number | null
    labelledBy: string | null
    labelledAt: string
    thread?: {
        mailbox: string | null
        caseId: string | null
        claimRef: string | null
        subject: string | null
        counterpartyAddress: string | null
        counterpartyKind: 'lender' | 'client' | 'other'
        state: string
        lastInboundAt: string | null
        outcomeAt: string | null
        lenderName: string | null
        clientName: string | null
        clientEmail: string | null
        introducer: string | null
        outcomeFrom: string | null
        outcomeSubject: string | null
        outcomeReceivedAt: string | null
    }
}

export interface DeepDiveRun {
    id: string
    parentKey: string
    mailbox: string | null
    bucket: string | null
    startedBy: string | null
    onlyNew: boolean
    threadsPlanned: number | null
    estCostPence: number | null
    estMinutes: number | null
    maxCostPence: number | null
    processed: number
    ok: number
    failed: number
    costPence: number
    newSubcategories: number
    needsCheck: number
    flagged: number
    startedAt: string
    finishedAt: string | null
    reason: 'complete' | 'budget' | 'stopped' | 'error' | null
    error: string | null
    durationMs: number | null
}

export interface DeepDiveJob {
    state: 'idle' | 'running' | 'stopping'
    running: boolean
    /** The running (or last) job is for this outcome in this scope. */
    thisParent: boolean
    parent: string | null
    params: Record<string, unknown> | null
    progress: { processed: number; ok: number; failed: number; remaining: number | null; costPence: number; note: string | null; live?: { planned?: number; newSubcategories?: number; needsCheck?: number; flagged?: number; runId?: string } | null } | null
    startedAt: string | null
    finishedAt: string | null
    finishedReason: 'complete' | 'stopped' | 'error' | 'budget' | null
    lastError: string | null
    runId: string | null
}

export interface RunnerState {
    mode: 'worker' | 'api' | string
    workerReachable: boolean
    workerConfigured: boolean
    canRun: boolean
    reason: 'worker_required' | 'worker_unavailable' | null
    worker?: { id: string | null; loopsRunning: boolean | null; coreVersion: string | null } | null
}

export interface DeepDiveStatus {
    parent: { key: string; label: string; description: string | null; affectsOutcome: boolean }
    scope: { mailbox: string | null; bucket: string | null }
    counts: { threads: number; labelled: number; pending: number; needsCheck: number; flagged: number }
    subcategories: Subcategory[]
    lastRun: DeepDiveRun | null
    job: DeepDiveJob | null
    runner: RunnerState
}

export interface DeepDivePreview {
    onlyNew: boolean
    threads: number
    tokens: { input: number; output: number }
    costPence: { low: number; likely: number; high: number }
    minutes: { likely: number; high: number }
    basis: 'observed' | 'estimate'
    observedRuns: number
    concurrency: number
    size: { avgOutcomeChars: number; avgThreadChars: number; avgMessages: number }
    models: { primary: string | null; fallback: string | null }
    subcategoriesSoFar: number
    suggestedCapPence: number
}

export interface DeepDiveScope {
    parent: string
    mailbox: string | null
    /** all | waiting | archived | letter:<kind>; the API reads 'all' as no filter. */
    bucket: string
}

const scopeBody = ({ parent, mailbox, bucket }: DeepDiveScope) => ({ parent, mailbox, bucket: bucket === 'all' ? null : bucket })

const unwrap = <T>(resp: { data?: any }, what: string): T => {
    if (!resp.data?.success) throw new Error(resp.data?.message || `Could not ${what}`)
    return resp.data as T
}

export const messageOf = (err: any, fallback: string): string => err?.response?.data?.message || err?.message || fallback

export const deepDiveApi = {
    status: (scope: DeepDiveScope) => api({ url: 'comms/deepdive/status', data: scopeBody(scope) }).then((r) => unwrap<DeepDiveStatus>(r, 'load the deep dive')),
    preview: (scope: DeepDiveScope, onlyNew = true) => api({ url: 'comms/deepdive/preview', data: { ...scopeBody(scope), onlyNew } }).then((r) => unwrap<DeepDivePreview>(r, 'build the preview')),
    start: (scope: DeepDiveScope, body: { onlyNew: boolean; maxCostPounds: number; estCostPence: number | null; estMinutes: number | null }) =>
        api({ url: 'comms/deepdive/start', data: { ...scopeBody(scope), ...body } }).then((r) => unwrap<{ ranOn: string }>(r, 'start the deep dive')),
    stop: () => api({ url: 'comms/pipeline/stop', data: { job: 'deepdive' } }).then((r) => unwrap<unknown>(r, 'stop the deep dive')),
    threads: (scope: DeepDiveScope, filter: { subcategoryId?: string | null; evidenceState?: EvidenceState | null; flagged?: boolean; limit?: number; offset?: number }) =>
        api({ url: 'comms/deepdive/threads', data: { ...scopeBody(scope), ...filter } }).then((r) => unwrap<{ threads: ThreadLabel[]; total: number; limit: number; offset: number }>(r, 'list the threads')),
    brief: (scope: DeepDiveScope, subcategoryId: string) => api({ url: 'comms/deepdive/subcategory-brief', data: { ...scopeBody(scope), subcategoryId } }).then((r) => unwrap<{ brief: SubcategoryBrief }>(r, 'build the brief')).then((r) => r.brief),
    createSubcategory: (scope: DeepDiveScope, body: { label: string; description?: string | null; suggestedAction?: Partial<SuggestedAction> | null }) =>
        api({ url: 'comms/deepdive/subcategory-create', data: { ...scopeBody(scope), ...body } }).then((r) => unwrap<{ subcategory: Subcategory }>(r, 'add the sub-category')),
    updateSubcategory: (id: string, patch: { label?: string; description?: string | null; suggestedAction?: Partial<SuggestedAction> | null }) =>
        api({ url: 'comms/deepdive/subcategory-update', data: { id, ...patch } }).then((r) => unwrap<{ subcategory: Subcategory }>(r, 'save the sub-category')),
    mergeSubcategory: (fromId: string, intoId: string) => api({ url: 'comms/deepdive/subcategory-merge', data: { fromId, intoId } }).then((r) => unwrap<{ moved: number; subcategory: Subcategory }>(r, 'merge the sub-categories')),
    relabelThread: (conversationId: string, parent: string, subcategoryId: string) => api({ url: 'comms/deepdive/thread-relabel', data: { conversationId, parent, subcategoryId } }).then((r) => unwrap<unknown>(r, 'move the thread')),
}

export const pounds = (pence: number | null | undefined, { approx = false }: { approx?: boolean } = {}) => {
    if (pence == null) return '—'
    const v = pence / 100
    const text = v >= 100 ? `£${Math.round(v).toLocaleString('en-GB')}` : `£${v.toFixed(2)}`
    return approx ? `~${text}` : text
}

export const minutesText = (m: number | null | undefined) => {
    if (m == null) return '—'
    if (m < 1) return 'under a minute'
    if (m < 60) return `${m} min`
    const h = Math.floor(m / 60)
    const rest = m % 60
    return rest ? `${h} h ${rest} min` : `${h} h`
}

export const FLAG_LABEL: Record<string, { text: string; hint: string }> = {
    injection_suspect: { text: 'Possible prompt injection', hint: 'The email contained wording that looks like an instruction to the model. The label was still checked against the email, but read it yourself.' },
    cost_outlier: { text: 'Cost outlier', hint: 'This email cost far more than the average for the run (very long, or many attachments).' },
    new_cap_hit: { text: 'New sub-category cap', hint: 'The model wanted a new sub-category but the run had already created its maximum; the thread was put under Unsorted (needs review).' },
}

export const EVIDENCE_LABEL: Record<EvidenceState, { text: string; hint: string }> = {
    verified: { text: 'Quote verified', hint: 'Every quote the model gave was found word for word in the email.' },
    needs_check: { text: 'Needs a check', hint: 'At least one quote could not be found in the email as written. Open the thread and read the email.' },
    human: { text: 'Set by a person', hint: 'A person placed this thread here.' },
}

// ---------------------------------------------------------------------------
// Merge suggestions: sub-categories that look like the same thing said twice.
// ---------------------------------------------------------------------------

/** Words that carry no meaning in a sub-category name ("Client identity details" vs "Client identity verification"). */
const NOISE_WORDS = new Set(['the', 'a', 'an', 'of', 'and', 'or', 'for', 'to', 'with', 'from', 'by', 'in', 'on', 'at', 'is', 'are', 'be', 'that', 'this', 'their', 'our', 'we', 'they', 'it', 'as', 'details', 'detail', 'information', 'info', 'request', 'requested', 'requests', 'required', 'requirement', 'requirements', 'confirmation', 'confirm', 'verification', 'verify', 'provide', 'provided', 'needed', 'need', 'further', 'additional', 'other', 'general', 'various', 'specific'])

/** Crude stemmer: enough to make "authority"/"authorities", "sign"/"signed"/"signing" agree. */
const stem = (w: string) => {
    let s = w
    if (s.length > 5 && s.endsWith('ies')) s = `${s.slice(0, -3)}y`
    else if (s.length > 5 && s.endsWith('ation')) s = s.slice(0, -5)
    else if (s.length > 4 && s.endsWith('ing')) s = s.slice(0, -3)
    else if (s.length > 4 && s.endsWith('ed')) s = s.slice(0, -2)
    else if (s.length > 3 && s.endsWith('s')) s = s.slice(0, -1)
    return s
}

export const meaningfulWords = (text: string | null | undefined): Set<string> => {
    const out = new Set<string>()
    for (const raw of (text ?? '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)) {
        if (!raw || raw.length < 2 || NOISE_WORDS.has(raw)) continue
        out.add(stem(raw))
    }
    return out
}

const jaccard = (a: Set<string>, b: Set<string>) => {
    if (!a.size || !b.size) return 0
    let inter = 0
    a.forEach((w) => {
        if (b.has(w)) inter++
    })
    return inter / (a.size + b.size - inter)
}

const containment = (a: Set<string>, b: Set<string>) => {
    const small = a.size <= b.size ? a : b
    const big = small === a ? b : a
    if (!small.size) return 0
    let inter = 0
    small.forEach((w) => {
        if (big.has(w)) inter++
    })
    return inter / small.size
}

export interface MergeSuggestion {
    /** The smaller one, proposed to be merged away. */
    from: Subcategory
    /** The larger one, proposed to keep. */
    into: Subcategory
    /** 0–1, higher = more alike. */
    score: number
    /** Words the two names share, for the explanation. */
    shared: string[]
}

/**
 * Pairs of active sub-categories whose names (and descriptions) say much the
 * same thing. Purely lexical, no model call: it is a prompt to look, and the
 * person decides. The bigger one is proposed as the survivor.
 */
export const suggestMerges = (subcategories: Subcategory[], { minScore = 0.45 }: { minScore?: number } = {}): MergeSuggestion[] => {
    const live = subcategories.filter((s) => s.active && !s.mergedIntoId && s.key !== 'unsorted')
    const words = new Map(live.map((s) => [s.id, { label: meaningfulWords(s.label), all: meaningfulWords(`${s.label} ${s.description ?? ''}`) }]))
    const out: MergeSuggestion[] = []
    for (let i = 0; i < live.length; i++) {
        for (let j = i + 1; j < live.length; j++) {
            const a = live[i]
            const b = live[j]
            const wa = words.get(a.id)!
            const wb = words.get(b.id)!
            const labelJ = jaccard(wa.label, wb.label)
            const labelC = containment(wa.label, wb.label)
            const descJ = jaccard(wa.all, wb.all)
            // Names that overlap heavily, or one name inside the other with descriptions that agree.
            const score = Math.max(labelJ, labelC >= 0.99 ? 0.6 + 0.4 * descJ : 0) * 0.7 + descJ * 0.3
            if (score < minScore) continue
            const shared: string[] = []
            wa.label.forEach((w) => {
                if (wb.label.has(w)) shared.push(w)
            })
            const [from, into] = (a.threads ?? 0) <= (b.threads ?? 0) ? [a, b] : [b, a]
            out.push({ from, into, score: Math.round(score * 100) / 100, shared })
        }
    }
    return out.sort((x, y) => y.score - x.score)
}

/**
 * Split `text` around each quote so the quotes can be marked. Matching is
 * tolerant of whitespace and case (the model may tidy line breaks); a quote
 * that is not found is simply not marked.
 */
export const splitOnQuotes = (text: string, quotes: string[]): { text: string; hit: boolean }[] => {
    const clean = quotes.map((q) => q.trim()).filter((q) => q.length >= 6)
    if (!text || !clean.length) return [{ text, hit: false }]
    const pattern = clean
        .sort((a, b) => b.length - a.length)
        .map((q) => q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+').replace(/['\u2018\u2019]/g, "['\u2018\u2019]").replace(/["\u201c\u201d]/g, '["\u201c\u201d]'))
        .join('|')
    let re: RegExp
    try {
        re = new RegExp(pattern, 'gi')
    } catch {
        return [{ text, hit: false }]
    }
    const parts: { text: string; hit: boolean }[] = []
    let last = 0
    let m: RegExpExecArray | null
    while ((m = re.exec(text)) !== null) {
        if (!m[0]) {
            re.lastIndex++
            continue
        }
        const at = m.index
        if (at > last) parts.push({ text: text.slice(last, at), hit: false })
        parts.push({ text: m[0], hit: true })
        last = at + m[0].length
    }
    if (last < text.length) parts.push({ text: text.slice(last), hit: false })
    return parts.length ? parts : [{ text, hit: false }]
}
